import React, { useState } from "react"

import { IconEmailSmall, IconPhoneSmall, colors, spacing } from "@izettle/otto"
import { SpinnerSimple } from "@izettle/react"
import { useLocalization } from "@izettle/tenko-react"

import { useModal, Modal, ModalType } from "@/core/components/modal"
import { Item, AnchorButton, SvgIcon, Content, Flex } from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { Phone } from "@/modules/sign-up/api/types"
import { useSendReceiptPost } from "@/modules/sign-up/api/use-send-receipt"
import ErrorDialog from "@/modules/sign-up/components/error-dialog"
import formatPhone from "@/modules/sign-up/shared/phone-format"

import { Modals, SlideIndexs } from "./types"

type Props = {
  purchaseUuid: string
  customerUuid: string | null
  phone?: Phone
  email?: string
}

export function SendReceiptOptionsModal({
  customerUuid,
  purchaseUuid,
  phone,
  email,
}: Props) {
  const { text } = useLocalization()
  const { goTo } = useSlider()
  const [, hideModal] = useModal(Modals.ContactOptions)
  const [dialogMessage, setDialogMessage] = useState("")

  const closeDialog = () => {
    setDialogMessage("")
  }

  const { mutate, isLoading } = useSendReceiptPost({
    onSuccess: ({ failureMessage, phone: successPhone, email: successEmail }) => {
      if (!failureMessage) {
        if (successPhone) {
          goTo(SlideIndexs.ContactSent, {
            phone: formatPhone(successPhone),
          })
        } else {
          goTo(SlideIndexs.ContactSent, { email: successEmail })
        }
      } else {
        setDialogMessage(`SIGNUP_ERROR_${failureMessage}`)
      }
      hideModal()
    },
  })

  const sendReceipt = (key: string, value?: string | Phone) => () => {
    if (customerUuid && purchaseUuid) {
      mutate({
        customerUuid,
        purchaseUuid,
        contactInfo: {
          [key]: value,
        },
      })
    } else {
      // [TODO] Remvove this and replace it with proper error message
      // eslint-disable-next-line no-console
      console.error("Missing customer", customerUuid)
    }
  }

  return (
    <Modal
      id={Modals.ContactOptions}
      title={text("SIGNUP_SEND_RECEIPT")}
      type={ModalType.Clip}
    >
      {isLoading ? (
        <Flex vCentered hCentered>
          <SpinnerSimple />
        </Flex>
      ) : (
        <Content>
          <Item>
            <SvgIcon
              html={IconEmailSmall.svg}
              color={colors.action.primary.border.default}
              margin={`0 ${spacing.small} 0 0`}
              aria-hidden
            />
            <AnchorButton onClick={sendReceipt("email", email)}>{email}</AnchorButton>
          </Item>
          <Item>
            <SvgIcon
              html={IconPhoneSmall.svg}
              color={colors.action.primary.border.default}
              margin={`0 ${spacing.small} 0 0`}
              aria-hidden
            />
            <AnchorButton onClick={sendReceipt("phone", phone)}>
              {formatPhone(phone)}
            </AnchorButton>
          </Item>
        </Content>
      )}
      <ErrorDialog onClose={closeDialog} message={dialogMessage} />
    </Modal>
  )
}
